type StorefrontHeadMeta = {
  title: string
  description: string
  keywords?: string
  author?: string
  canonicalUrl: string
  ogImage?: string
  logo?: string
  siteName: string
  type?: "website" | "product"
  jsonLd?: object
}

function upsertMeta(selector: string, attrs: Record<string, string>) {
  if (typeof document === "undefined") return
  let el = document.head.querySelector(selector) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement("meta")
    document.head.appendChild(el)
  }
  Object.entries(attrs).forEach(([key, value]) => {
    el!.setAttribute(key, value)
  })
}

function removeMeta(selector: string) {
  if (typeof document === "undefined") return
  document.head.querySelectorAll(selector).forEach((el) => el.remove())
}

function upsertLink(selector: string, attrs: Record<string, string>) {
  if (typeof document === "undefined") return
  let el = document.head.querySelector(selector) as HTMLLinkElement | null
  if (!el) {
    el = document.createElement("link")
    document.head.appendChild(el)
  }
  Object.entries(attrs).forEach(([key, value]) => {
    el!.setAttribute(key, value)
  })
}

function upsertJsonLd(id: string, data?: Record<string, unknown>) {
  if (typeof document === "undefined") return
  const existing = document.getElementById(id)
  if (!data) {
    existing?.remove()
    return
  }
  const script = (existing as HTMLScriptElement | null) || document.createElement("script")
  script.id = id
  script.type = "application/ld+json"
  script.textContent = JSON.stringify(data).replace(/</g, "\\u003c")
  if (!existing) document.head.appendChild(script)
}

export function applyStorefrontHead(meta: StorefrontHeadMeta) {
  if (typeof document === "undefined") return

  document.title = meta.title

  upsertMeta('meta[name="description"]', {
    name: "description",
    content: meta.description,
  })

  if (meta.keywords) {
    upsertMeta('meta[name="keywords"]', {
      name: "keywords",
      content: meta.keywords,
    })
  }

  upsertMeta('meta[name="author"]', {
    name: "author",
    content: meta.author || meta.siteName,
  })

  upsertMeta('meta[name="robots"]', {
    name: "robots",
    content: "index, follow",
  })

  upsertMeta('meta[property="og:type"]', {
    property: "og:type",
    content: meta.type || "website",
  })
  upsertMeta('meta[property="og:url"]', {
    property: "og:url",
    content: meta.canonicalUrl,
  })
  upsertMeta('meta[property="og:title"]', {
    property: "og:title",
    content: meta.title,
  })
  upsertMeta('meta[property="og:description"]', {
    property: "og:description",
    content: meta.description,
  })
  upsertMeta('meta[property="og:site_name"]', {
    property: "og:site_name",
    content: meta.siteName,
  })
  upsertMeta('meta[property="og:locale"]', {
    property: "og:locale",
    content: "en_US",
  })

  upsertMeta('meta[name="twitter:card"]', {
    name: "twitter:card",
    content: "summary_large_image",
  })
  upsertMeta('meta[name="twitter:url"]', {
    name: "twitter:url",
    content: meta.canonicalUrl,
  })
  upsertMeta('meta[name="twitter:title"]', {
    name: "twitter:title",
    content: meta.title,
  })
  upsertMeta('meta[name="twitter:description"]', {
    name: "twitter:description",
    content: meta.description,
  })

  if (meta.ogImage) {
    upsertMeta('meta[property="og:image"]', {
      property: "og:image",
      content: meta.ogImage,
    })
    upsertMeta('meta[property="og:image:width"]', {
      property: "og:image:width",
      content: "1200",
    })
    upsertMeta('meta[property="og:image:height"]', {
      property: "og:image:height",
      content: "630",
    })
    upsertMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: meta.title,
    })
    upsertMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: meta.ogImage,
    })
  } else {
    removeMeta('meta[property="og:image"]')
    removeMeta('meta[property="og:image:width"]')
    removeMeta('meta[property="og:image:height"]')
    removeMeta('meta[property="og:image:alt"]')
    removeMeta('meta[name="twitter:image"]')
  }

  upsertLink('link[rel="canonical"]', {
    rel: "canonical",
    href: meta.canonicalUrl,
  })

  if (meta.logo) {
    upsertLink('link[rel="icon"]', {
      rel: "icon",
      href: meta.logo,
    })
  }

  upsertJsonLd("store-jsonld", meta.jsonLd as Record<string, unknown> | undefined)
}

export function storeCanonicalUrl(storeUrl: string) {
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/${storeUrl}`
  }
  return `https://sqale.shop/${storeUrl}`
}

export function stripHtml(value?: string) {
  if (!value) return ""
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
}
